import type { JSX } from 'react';

import type { Tile as TileSchema } from '@schema/tiles.js';

export interface TileProps {
  tile: TileSchema;
  onActivate: (tile: TileSchema) => void;
  /** Optional label override, e.g., "(no agent)" while a dispatch is failing. */
  statusHint?: string | undefined;
}

/**
 * Single launcher tile. Renders an icon + label and calls `onActivate`
 * on click/Enter/Space. Spacer tiles render an invisible cell so the grid
 * keeps its layout but the cell is non-interactive.
 *
 * Icon resolution order:
 *   1. `iconDataUrl` — inline data URL from the Builder's uploader.
 *   2. `iconRef` from config → loaded as an asset (production path).
 *   3. Fallback: a generated colored "letter avatar" derived from the
 *      label. Demo deployments and migrations from the old launcher get
 *      consistent, recognizable visuals without per-app icon assets.
 *
 * `showLabel` defaults to true; set to false to render an icon-only tile.
 */
export function Tile({ tile, onActivate, statusHint }: TileProps): JSX.Element {
  if (tile.type === 'spacer') {
    return <div className="tile tile--spacer" aria-hidden="true" data-testid={`tile-${tile.id}`} />;
  }

  if (tile.type === 'divider') {
    // Row-spanning accent bar. CSS gives it grid-column: 1 / -1 to
    // span every column of its row. Color + height come from config
    // with sane defaults that match the launcher's accent + a
    // touch-target-friendly 8px bar.
    const height = tile.height ?? 8;
    const color = tile.color ?? 'var(--ml-accent, #03dac6)';
    return (
      <div
        className="tile tile--divider"
        role={tile.label !== undefined ? 'separator' : 'presentation'}
        {...(tile.label !== undefined && { 'aria-label': tile.label })}
        data-testid={`tile-${tile.id}`}
        style={{ height: `${String(height)}px`, background: color }}
      />
    );
  }

  const handleActivate = (): void => {
    onActivate(tile);
  };

  return (
    <button
      type="button"
      className={`tile tile--${tile.type}`}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
      data-testid={`tile-${tile.id}`}
      aria-label={tile.label}
    >
      {tile.iconDataUrl !== undefined ? (
        <img className="tile__icon" src={tile.iconDataUrl} alt="" />
      ) : tile.iconRef !== undefined ? (
        <img className="tile__icon" src={resolveAsset(tile.iconRef)} alt="" />
      ) : tile.type === 'folder' ? (
        <FolderIcon />
      ) : (
        <GeneratedIcon label={tile.label} />
      )}
      {tile.showLabel !== false && <span className="tile__label">{tile.label}</span>}
      {statusHint !== undefined && <span className="tile__status">{statusHint}</span>}
    </button>
  );
}

/**
 * Colored rounded-square with the label's leading character. Color is a
 * stable function of the label so the visual stays put across reloads
 * without us needing to ship per-app icons.
 */
function GeneratedIcon({ label }: { label: string }): JSX.Element {
  const palette = [
    '#1976d2', // blue
    '#03a9b3', // teal
    '#7c4dff', // purple
    '#e85d75', // pink-red
    '#f57c00', // orange
    '#43a047', // green
    '#5e35b1', // deep purple
    '#0288d1', // cyan
    '#d81b60', // magenta
    '#fb8c00', // amber
    '#00897b', // dark teal
    '#3949ab', // indigo
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) | 0;
  }
  const color = palette[Math.abs(hash) % palette.length] ?? palette[0]!;
  const initial = (label.trim().charAt(0) || '?').toUpperCase();
  return (
    <span
      className="tile__icon tile__icon--generated"
      style={{ background: color }}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

/**
 * Folder fallback icon — neutral colored square with a folder
 * glyph, sized to match GeneratedIcon so the grid stays uniform.
 * Admins can still set iconRef / iconDataUrl on a folder tile to
 * override this; the fallback only renders when no icon is set.
 */
function FolderIcon(): JSX.Element {
  return (
    <span
      className="tile__icon tile__icon--generated tile__icon--folder"
      style={{ background: '#5b6b85' }}
      aria-hidden="true"
    >
      <svg
        width="60%"
        height="60%"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ color: '#ffffff' }}
      >
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
      </svg>
    </span>
  );
}

/**
 * Resolve an AssetRef to a fetchable URL. In the demo the assets directory
 * sits next to `index.html` under `/assets/`. Production resolves against
 * the asset bundle path the build pipeline emits.
 */
function resolveAsset(ref: string): string {
  return `/assets/${ref}`;
}
